import React, { useState } from 'react';
import { CheckCircle2, AlertCircle, Clock, X, Save } from 'lucide-react';
import GlobalTable from '../utils/GlobalTable';

const initialEquipos = [
  {
    id: 1,
    medidorNo: 'MM-01',
    planta: 'PLANTA COPEC IQUIQUE',
    mesa: '200',
    brazo: '2',
    modelo: 'F4-S1',
    serie: 'X9823-A',
    unidad: 'Litros',
    producto: 'PD',
    factorActual: '0.99806',
    ultimaCalibracion: '2023-12-13',
    proximaCalibracion: '2024-12-13',
    estado: 'Vigente'
  },
  {
    id: 2,
    medidorNo: 'MM-02',
    planta: 'PLANTA COPEC IQUIQUE',
    mesa: '200',
    brazo: '1',
    modelo: 'M-150',
    serie: 'B7721-C',
    unidad: 'Litros',
    producto: 'G93',
    factorActual: '1.00042',
    ultimaCalibracion: '2023-11-20',
    proximaCalibracion: '2024-11-20',
    estado: 'Próximo a vencer'
  },
  {
    id: 3,
    medidorNo: 'MM-03',
    planta: 'PLANTA ANTOFAGASTA',
    mesa: '100',
    brazo: '3',
    modelo: 'T-200',
    serie: 'Z4412-B',
    unidad: 'M3',
    producto: 'PD',
    factorActual: '0.99915',
    ultimaCalibracion: '2023-01-15',
    proximaCalibracion: '2024-01-15',
    estado: 'Vencido'
  },
  {
    id: 4,
    medidorNo: 'MP-01',
    planta: 'PLANTA COPEC IQUIQUE',
    mesa: 'Patrón Móvil',
    brazo: '-',
    modelo: 'Master Meter Brodie',
    serie: 'MM-49102',
    unidad: 'Litros',
    producto: 'Multiproducto',
    factorActual: '1.00130',
    ultimaCalibracion: '2024-02-10',
    proximaCalibracion: '2025-02-10',
    estado: 'Vigente'
  }
];

const emptyForm = {
  medidorNo: '',
  planta: 'PLANTA COPEC IQUIQUE',
  mesa: '',
  brazo: '',
  modelo: '',
  serie: '',
  unidad: 'Litros',
  producto: 'PD',
  factorActual: '1.00000',
  ultimaCalibracion: new Date().toISOString().split('T')[0],
  proximaCalibracion: '',
  estado: 'Vigente'
};

const InventarioEquipos = () => {
  const [equipos, setEquipos] = useState(initialEquipos);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    if (!window.confirm(`¿Desea eliminar el medidor ${item.medidorNo} (${item.serie})?`)) return;
    setEquipos((prev) => prev.filter((e) => e.id !== item.id));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!formData.medidorNo || !formData.serie) {
      alert('Complete el N° de Medidor y Número de Serie.');
      return;
    }
    if (editingItem) {
      setEquipos((prev) =>
        prev.map((eq) => (eq.id === editingItem.id ? { ...formData, id: editingItem.id } : eq))
      );
    } else {
      const nextId = equipos.length > 0 ? Math.max(...equipos.map((e) => e.id)) + 1 : 1;
      setEquipos((prev) => [...prev, { ...formData, id: nextId }]);
    }
    setIsModalOpen(false);
  };

  const getStatusBadge = (estado) => {
    const norm = String(estado || '').toLowerCase();
    if (norm.includes('vigente')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200">
          <CheckCircle2 className="w-3.5 h-3.5" /> Vigente
        </span>
      );
    }
    if (norm.includes('próximo') || norm.includes('proximo')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200">
          <Clock className="w-3.5 h-3.5" /> Próximo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-red-100 text-red-800 border border-red-200">
        <AlertCircle className="w-3.5 h-3.5" /> Vencido
      </span>
    );
  };

  const filteredByStatus =
    statusFilter === 'ALL'
      ? equipos
      : equipos.filter((e) => e.estado.toLowerCase().includes(statusFilter.toLowerCase()));

  const columns = [
    {
      header: 'Medidor N°',
      accessor: 'medidorNo',
      render: (row) => (
        <span className="font-bold text-[#005f88] font-mono">{row.medidorNo}</span>
      )
    },
    { header: 'Planta / Ubicación', accessor: 'planta' },
    {
      header: 'Mesa / Brazo',
      accessor: 'mesa',
      render: (row) => (
        <span className="text-slate-700">
          Mesa <strong>{row.mesa}</strong> {row.brazo !== '-' ? `/ Brazo ${row.brazo}` : ''}
        </span>
      )
    },
    { header: 'Modelo', accessor: 'modelo' },
    {
      header: 'N° Serie',
      accessor: 'serie',
      render: (row) => <span className="font-mono text-xs text-slate-600">{row.serie}</span>
    },
    {
      header: 'Producto / U.M.',
      accessor: 'producto',
      render: (row) => (
        <span>
          <strong className="text-slate-800">{row.producto}</strong>{' '}
          <span className="text-xs text-slate-500">({row.unidad})</span>
        </span>
      )
    },
    {
      header: 'Factor Actual',
      accessor: 'factorActual',
      render: (row) => (
        <span className="font-mono font-bold text-[#0A6ED1]">{row.factorActual}</span>
      )
    },
    { header: 'Última Calib.', accessor: 'ultimaCalibracion' },
    { header: 'Próxima Calib.', accessor: 'proximaCalibracion' },
    {
      header: 'Estado',
      accessor: 'estado',
      render: (row) => getStatusBadge(row.estado)
    }
  ];

  const countVigente = equipos.filter((e) => e.estado === 'Vigente').length;
  const countProximo = equipos.filter((e) => e.estado.includes('Próximo')).length;
  const countVencido = equipos.filter((e) => e.estado === 'Vencido').length;

  const statusFilterPills = (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button
        type="button"
        onClick={() => setStatusFilter('ALL')}
        className={`px-2.5 py-1 rounded text-xs font-bold border transition-colors ${
          statusFilter === 'ALL'
            ? 'bg-[#005f88] text-white border-[#004a6b]'
            : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
        }`}
      >
        Todos ({equipos.length})
      </button>
      <button
        type="button"
        onClick={() => setStatusFilter('Vigente')}
        className={`px-2.5 py-1 rounded text-xs font-bold border transition-colors flex items-center gap-1.5 ${
          statusFilter === 'Vigente'
            ? 'bg-emerald-600 text-white border-emerald-700'
            : 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
        }`}
      >
        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
        Vigentes ({countVigente})
      </button>
      <button
        type="button"
        onClick={() => setStatusFilter('Próximo')}
        className={`px-2.5 py-1 rounded text-xs font-bold border transition-colors flex items-center gap-1.5 ${
          statusFilter === 'Próximo'
            ? 'bg-amber-500 text-white border-amber-600'
            : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
        }`}
      >
        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
        Próximos ({countProximo})
      </button>
      <button
        type="button"
        onClick={() => setStatusFilter('Vencido')}
        className={`px-2.5 py-1 rounded text-xs font-bold border transition-colors flex items-center gap-1.5 ${
          statusFilter === 'Vencido'
            ? 'bg-red-600 text-white border-red-700'
            : 'bg-red-50 text-red-800 border-red-200 hover:bg-red-100'
        }`}
      >
        <span className="w-2 h-2 rounded-full bg-red-500"></span>
        Vencidos ({countVencido})
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full">
      <GlobalTable
        storageKey="inventario_equipos_calib"
        columns={columns}
        data={filteredByStatus}
        extraActions={statusFilterPills}
        onAdd={handleOpenCreate}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
        searchPlaceholder="Buscar medidor, serie, planta..."
      />

      {/* Modal Crear / Editar Equipo */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl border border-[#005f88] w-full max-w-2xl overflow-hidden">
            <div className="bg-[#005f88] text-white px-4 py-3 flex items-center justify-between border-b border-[#004a6b]">
              <h3 className="font-bold text-sm uppercase tracking-wider">
                {editingItem ? `Editar Equipo: ${editingItem.medidorNo}` : 'Registrar Nuevo Equipo / Medidor'}
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-cyan-100 hover:text-white p-1 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-4 space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Medidor N° *</label>
                  <input
                    type="text"
                    required
                    value={formData.medidorNo}
                    onChange={(e) => setFormData({ ...formData, medidorNo: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                    placeholder="Ej: MM-01"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Modelo *</label>
                  <input
                    type="text"
                    required
                    value={formData.modelo}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                    placeholder="Ej: F4-S1"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">N° Serie *</label>
                  <input
                    type="text"
                    required
                    value={formData.serie}
                    onChange={(e) => setFormData({ ...formData, serie: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                    placeholder="Ej: X9823-A"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Planta / Ubicación</label>
                  <input
                    type="text"
                    value={formData.planta}
                    onChange={(e) => setFormData({ ...formData, planta: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Mesa de Carga</label>
                  <input
                    type="text"
                    value={formData.mesa}
                    onChange={(e) => setFormData({ ...formData, mesa: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Brazo</label>
                  <input
                    type="text"
                    value={formData.brazo}
                    onChange={(e) => setFormData({ ...formData, brazo: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Producto</label>
                  <input
                    type="text"
                    value={formData.producto}
                    onChange={(e) => setFormData({ ...formData, producto: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Unidad de Medida</label>
                  <select
                    value={formData.unidad}
                    onChange={(e) => setFormData({ ...formData, unidad: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none bg-white"
                  >
                    <option value="Litros">Litros</option>
                    <option value="M3">M3</option>
                    <option value="Galones">Galones</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Factor Actual</label>
                  <input
                    type="text"
                    value={formData.factorActual}
                    onChange={(e) => setFormData({ ...formData, factorActual: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Última Calibración</label>
                  <input
                    type="date"
                    value={formData.ultimaCalibracion}
                    onChange={(e) => setFormData({ ...formData, ultimaCalibracion: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Próxima Calibración</label>
                  <input
                    type="date"
                    value={formData.proximaCalibracion}
                    onChange={(e) => setFormData({ ...formData, proximaCalibracion: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Estado</label>
                  <select
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none bg-white"
                  >
                    <option value="Vigente">Vigente</option>
                    <option value="Próximo a vencer">Próximo a vencer</option>
                    <option value="Vencido">Vencido</option>
                  </select>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded bg-[#005f88] hover:bg-[#004a6b] text-white font-bold flex items-center gap-1.5 shadow-sm"
                >
                  <Save className="w-3.5 h-3.5" />
                  Guardar Equipo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventarioEquipos;
